import React, { useMemo, useState } from 'react';
import { DatePickerPopover } from './DatePickerPopover';
import PropertySelect from './PropertySelect';

type PlatformOption = { label: string; value: string; icon?: string; name?: string };

type ClientDetailsFilters = {
    startDate?: string;
    endDate?: string;
    provider?: string;
};

const inputBase =
    'w-full rounded-xl border bg-white dark:bg-black px-3 py-3 text-sm text-black dark:text-white border-gray-200 dark:border-borderColor-darkMode ' +
    'focus:outline-none focus:ring-0 focus:ring-offset-0 focus:border-gray-300 dark:focus:border-gray-600';

// const btnBase =
//     'rounded-xl text-sm border transition-all focus:outline-none focus:ring-0 focus:ring-offset-0';

const ChevronDown = () => (
    <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
        <path
            d='M6 9l6 6 6-6'
            stroke='currentColor'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </svg>
);

const platformBtn =
    inputBase + ' flex items-center justify-between gap-3 !py-3';

const menuBase =
    'absolute z-50 mt-2 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-borderColor-darkMode dark:bg-black';

const menuItemBase =
    'w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-white/5';

const menuItemActive =
    'bg-gray-50 dark:bg-white/5';

const PlatformSelect = ({
    value,
    options,
    onChange,
}: {
    value?: string;
    options: PlatformOption[];
    onChange: (next?: string) => void;
}) => {
    const [open, setOpen] = useState(false);

    const selected = useMemo(() => {
        if (!value) return options[0];
        return options.find((o) => o?.name === value) || options[0];
    }, [options, value]);

    return (
        <div
            className='relative'
            tabIndex={0}
            onBlur={(e) => {
                const next = e.relatedTarget as HTMLElement | null;
                if (!next || !e.currentTarget.contains(next)) setOpen(false);
            }}
        >
            <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>Platform</p>
            <button
                type='button'
                className={platformBtn}
                onClick={() => setOpen((s) => !s)}
            >
                <span className='flex items-center gap-2 min-w-0'>
                    {!!selected?.icon && (
                        <img
                            src={selected.icon}
                            alt={selected.label}
                            className='h-5 w-5 rounded-sm object-contain'
                        />
                    )}
                    <span className='truncate'>{selected?.label}</span>
                </span>
                <span className='text-gray-400'>
                    <ChevronDown />
                </span>
            </button>

            {open && (
                <div className={menuBase} role='listbox'>
                    {options.map((opt) => {
                        const isActive = (value ? value === opt.name : opt.name === options[0].name);
                        return (
                            <button
                                key={opt.name}
                                type='button'
                                role='option'
                                aria-selected={isActive}
                                className={`${menuItemBase} ${isActive ? menuItemActive : ''}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    setOpen(false);
                                    onChange(opt.name === options[0].name ? undefined : opt.name);
                                }}
                            >
                                {!!opt.icon && (
                                    <img
                                        src={opt.icon}
                                        alt={opt.label}
                                        className='h-5 w-5 rounded-sm object-contain'
                                    />
                                )}
                                <span className='truncate'>{opt.label}</span>
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const ClientCostsDetailsFiltersBar = ({
    properties,
    setSelection,
    selection,
    value,
    platforms,
    onChange,
    onApply,
    loading,
}: {
    properties: any;
    setSelection: any;
    selection: any;
    value: ClientDetailsFilters;
    platforms: PlatformOption[];
    onChange: (next: ClientDetailsFilters) => void;
    onApply: () => void;
    loading?: boolean;
}) => {
    const [pickerMode, setPickerMode] = useState<'single' | 'range'>(value.endDate ? 'range' : 'single');

    const platformOptions = useMemo(() => [{ label: 'All platforms', value: 'all' }, ...platforms], [platforms]);

    const setDates = (next: { startDate?: string; endDate?: string }) => {
        if (pickerMode === 'single') {
            onChange({ ...value, startDate: next.startDate, endDate: undefined });
            return;
        }
        onChange({ ...value, startDate: next.startDate, endDate: next.endDate });
    };

    return (
        <div className='w-full rounded-2xl border border-gray-200 dark:border-borderColor-darkMode bg-white dark:bg-black p-4'>
            <p className='text-primary-light mb-3 text-[17px]'>Filters</p>
            <div className='grid grid-cols-1 md:grid-cols-3 xl:grid-cols-12 gap-3 items-center'>
                <div className='xl:col-span-4'>
                    <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>Date</p>
                    <DatePickerPopover
                        mode={pickerMode}
                        startDate={value.startDate}
                        endDate={value.endDate}
                        onModeChange={(m: any) => {
                            setPickerMode(m);
                            if (m === 'single') onChange({ ...value, endDate: undefined });
                            else onChange({ ...value, startDate: value.startDate, endDate: value.endDate });
                        }}
                        onChange={setDates}
                    />
                </div>

                <div className='xl:col-span-4'>
                    <PlatformSelect
                        value={value.provider}
                        options={platformOptions}
                        onChange={(next) => onChange({ ...value, provider: next })}
                    />
                </div>
                <div className='xl:col-span-4'>
                    <p className='text-xs text-gray-500 dark:text-gray-400 mb-1'>Property</p>
                    <PropertySelect
                        disabled={false}
                        properties={properties}
                        selection={selection}
                        setSelection={setSelection}
                    />
                </div>

                {/* <div className='md:col-span-3 xl:col-span-12 flex justify-end mt-6'>
                    <button
                        type='button'
                        onClick={onApply}
                        disabled={loading}
                        className={`${btnBase} px-4 py-2 ${loading
                            ? 'opacity-60 cursor-not-allowed bg-[#00330f] text-white border-[#00330f] dark:bg-white dark:text-black dark:border-white'
                            : 'bg-[#00330f] text-white border-[#00330f] hover:opacity-90 dark:bg-white dark:text-black dark:border-white'
                            }`}
                    >
                        {loading ? 'Loading...' : 'Apply filters'}
                    </button>
                </div> */}
            </div>
        </div>
    );
};

export type { ClientDetailsFilters, PlatformOption };
export default ClientCostsDetailsFiltersBar;